<app-modal
  [title]="'profile.title' | transloco"
  icon="bi bi-person-circle"
  [isLoading]="isLoading()"
  [errorMessage]="errorMessage() || null"
  [confirmLabel]="null"
  [cancelLabel]="'profile.cancelLabel' | transloco"
  (confirm)="modal.close()"
  (cancelled)="modal.close()"
>
  @if (user()) {
    <!-- Avatar, Nome e Role -->
    <div class="text-center mb-4">
      @if (user()!.avatar) {
        <img
          [src]="user()!.avatar"
          [alt]="user()!.nome"
          class="rounded-circle mb-3"
          width="80"
          height="80"
          style="object-fit: cover"
        />
      } @else {
        <div
          class="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3 bg-primary-custom"
          style="width: 80px; height: 80px; color: white; font-size: 2rem; font-weight: bold"
        >
          {{ (user()!.nome.charAt(0) || 'U').toUpperCase() }}
        </div>
      }
      <h5 class="fw-bold mb-1 text-break">{{ user()!.nome }}</h5>
      <span class="badge" [class]="getRoleBadgeClass(user()!.role)">
        {{ getRoleLabel(user()!.role) }}
      </span>
    </div>

    <!-- Informações do usuário -->
    <div class="list-group list-group-flush">
      <div class="list-group-item bg-transparent d-flex align-items-center px-0">
        <i class="bi bi-envelope me-3 text-secondary-brand" style="width: 20px"></i>
        <div>
          <small class="text-secondary-brand d-block">{{ 'profile.emailLabel' | transloco }}</small>
          <span class="text-secondary-brand text-break">{{ user()!.email }}</span>
        </div>
      </div>

      <div class="list-group-item bg-transparent d-flex align-items-center px-0">
        <i class="bi bi-card-text me-3 text-secondary-brand" style="width: 20px"></i>
        <div>
          <small class="text-secondary-brand d-block">{{ 'profile.cpfLabel' | transloco }}</small>
          @if (user()!.cpf) {
            <span class="text-secondary-brand">{{ user()!.cpf! | mask: '000.000.000-00' }}</span>
          } @else {
            <span class="text-secondary-brand fst-italic">{{ 'profile.notInformed' | transloco }}</span>
          }
        </div>
      </div>

      <div class="list-group-item bg-transparent d-flex align-items-center px-0">
        <i class="bi bi-building me-3 text-secondary-brand" style="width: 20px"></i>
        <div>
          <small class="text-secondary-brand d-block">{{ 'profile.cnpjLabel' | transloco }}</small>
          @if (user()!.cnpj) {
            <span class="text-secondary-brand">{{ user()!.cnpj! | mask: '00.000.000/0000-00' }}</span>
          } @else {
            <span class="text-secondary-brand fst-italic">{{ 'profile.notInformed' | transloco }}</span>
          }
        </div>
      </div>

      <div class="list-group-item bg-transparent d-flex align-items-center px-0">
        <i class="bi bi-telephone me-3 text-secondary-brand" style="width: 20px"></i>
        <div>
          <small class="text-secondary-brand d-block">{{ 'profile.phoneLabel' | transloco }}</small>
          @if (user()!.telefone) {
            <span class="text-secondary-brand">{{ user()!.telefone! | mask: '(00) 00000-0000' }}</span>
          } @else {
            <span class="text-secondary-brand fst-italic">{{ 'profile.notInformed' | transloco }}</span>
          }
        </div>
      </div>

      <div class="list-group-item bg-transparent d-flex align-items-center px-0">
        <i
          class="bi bi-circle-fill me-3"
          [class.text-success]="user()!.isActive"
          [class.text-danger]="!user()!.isActive"
          style="width: 20px; font-size: 0.6rem"
        ></i>
        <div>
          <small class="text-secondary-brand d-block">{{ 'profile.statusLabel' | transloco }}</small>
          <span class="text-secondary-brand">{{
            user()!.isActive ? ('profile.statusActive' | transloco) : ('profile.statusInactive' | transloco)
          }}</span>
        </div>
      </div>

      @if (user()!.createdAt) {
        <div class="list-group-item bg-transparent d-flex align-items-center px-0 border-bottom-0">
          <i class="bi bi-calendar me-3 text-secondary-brand" style="width: 20px"></i>
          <div>
            <small class="text-secondary-brand d-block">{{ 'profile.memberSinceLabel' | transloco }}</small>
            <span class="text-secondary-brand">{{ user()!.createdAt | date: 'dd/MM/yyyy' }}</span>
          </div>
        </div>
      }
    </div>

    <!-- Seção de tier (apenas TomadorCredito no próprio perfil) -->
    @if (isTomadorCredito() && isOwnProfile() && tierInfo(); as tier) {
      <div class="mt-4 pt-3 border-top">
        <div class="d-flex align-items-center gap-2 mb-3">
          <i class="bi bi-layers text-primary-brand"></i>
          <span class="text-uppercase small fw-semibold text-secondary-brand">
            {{ 'profile.tier.sectionTitle' | transloco }}
          </span>
        </div>

        <!-- Tier atual e desconto -->
        <div class="row g-2 mb-3">
          <div class="col-6">
            <div class="card border-0 h-100">
              <div class="card-body bg-color-surface-lighter p-3">
                <small class="text-primary-brand d-block mb-1">{{ 'profile.tier.currentTier' | transloco }}</small>
                <span class="fw-semibold text-secondary-brand">
                  {{ tier.currentTierName || ('profile.tier.noTier' | transloco) }}
                </span>
              </div>
            </div>
          </div>
          <div class="col-6">
            <div class="card border-0 h-100">
              <div class="card-body bg-color-surface-lighter p-3">
                <small class="text-primary-brand d-block mb-1">{{ 'profile.tier.discount' | transloco }}</small>
                <span class="fw-semibold text-secondary-brand">
                  @if (tier.hasContractedTier && tier.currentDiscountPercentage > 0) {
                    {{ tier.currentDiscountPercentage }}%
                  } @else {
                    {{ 'profile.tier.noDiscount' | transloco }}
                  }
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Atividade na janela -->
        <div class="small text-secondary-brand mb-2">
          <i class="bi bi-clock-history me-1"></i>
          {{ 'profile.tier.operationsInWindow' | transloco: { count: tier.operationsInWindow, days: tier.windowDays } }}
        </div>

        <!-- Progresso / status -->
        @if (tier.isAtContractedCeiling && !tier.blockedByContractTierName) {
          <div class="alert alert-success py-2 px-3 mb-2 small">
            <i class="bi bi-trophy me-1"></i>
            {{ 'profile.tier.atMaxTier' | transloco: { name: tier.contractedTierName } }}
          </div>
        } @else if (tier.isAtContractedCeiling) {
          <div class="alert alert-info py-2 px-3 mb-2 small">
            <i class="bi bi-lock me-1"></i>
            {{ 'profile.tier.atCeiling' | transloco: { name: tier.contractedTierName } }}
          </div>
        } @else if (!tier.hasContractedTier) {
          <div class="alert alert-secondary py-2 px-3 mb-2 small">
            <i class="bi bi-info-circle me-1"></i>
            {{ 'profile.tier.noContractedTier' | transloco }}
          </div>
        } @else if (tier.nextTierName && tier.operationsToNextTier !== null) {
          <div class="small text-secondary-brand mb-2">
            <i class="bi bi-arrow-up-circle me-1 text-success"></i>
            {{ 'profile.tier.nextTier' | transloco: { count: tier.operationsToNextTier, name: tier.nextTierName } }}
          </div>
        }

        <!-- Nota sobre janela móvel -->
        <div class="text-secondary-brand" style="font-size: 0.75rem; opacity: 0.7">
          {{ 'profile.tier.windowNote' | transloco: { days: tier.windowDays } }}
        </div>
      </div>
    }
  }
</app-modal>
